import React, { useState, useRef, useCallback } from "react";

const SURFACE = "#141412";
const BORDER = "#242220";
const TEXT = "#edeae2";
const MUTED = "#8a8680";
const DIM = "#585450";
const BG = "#000";

const SVG = (paths) => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    {paths}
  </svg>
);

const INTENTS = [
  {
    value: "buy_self",
    label: "Buy for myself",
    icon: SVG(<><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></>),
    desc: "Full assessment, market value, seller questions",
  },
  {
    value: "flip",
    label: "Buy to flip",
    icon: SVG(<><path d="M7 16V4m0 0L3 8m4-4 4 4"/><path d="M17 8v12m0 0 4-4m-4 4-4-4"/></>),
    desc: "Flip margin estimate, condition focus",
  },
  {
    value: "own",
    label: "Already own it",
    icon: SVG(<><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></>),
    desc: "Current resale value, no seller questions",
  },
  {
    value: "reassess",
    label: "Reassess to sell",
    icon: SVG(<><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></>),
    desc: "Sale price estimate, condition flags",
  },
  {
    value: "mates_gear",
    label: "Mates Gear",
    icon: SVG(<><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/></>),
    desc: "Model overview and setup profile only",
  },
];

const OWNED_INTENTS = ["own", "reassess"];

function conditionColor(c) {
  return { Excellent: "#34c759", Good: "#7ecb8f", Fair: "#f59e0b", Poor: "#ef4444" }[c] || TEXT;
}
function conditionBg(c) {
  return { Excellent: "rgba(52,199,89,0.12)", Good: "rgba(126,203,143,0.12)", Fair: "rgba(245,158,11,0.12)", Poor: "rgba(239,68,68,0.12)" }[c] || "transparent";
}
function confidenceColor(s) {
  if (s >= 80) return "#34c759";
  if (s >= 60) return "#f59e0b";
  return "#ef4444";
}
function confidenceBg(s) {
  if (s >= 80) return "rgba(52,199,89,0.12)";
  if (s >= 60) return "rgba(245,158,11,0.12)";
  return "rgba(239,68,68,0.12)";
}

function ResultCard({ header, children }) {
  return (
    <div style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 14, overflow: "hidden" }}>
      <div style={{ padding: "11px 16px", borderBottom: `1px solid ${BORDER}` }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: DIM }}>{header}</div>
      </div>
      <div style={{ padding: "14px 16px" }}>{children}</div>
    </div>
  );
}

function KVRow({ label, value, last, color }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "8px 0", borderBottom: last ? "none" : `1px solid ${BORDER}`, gap: 12 }}>
      <span style={{ fontSize: 12, color: DIM, flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 600, color: color || TEXT, textAlign: "right" }}>{value}</span>
    </div>
  );
}

export default function EvaluatePage({ onNavigate, market, userSettings, assessTarget, onSaveAssessment, accentColor, accentRgb, initialData }) {
  const accent = accentColor || "#E8621A";
  const aRgb = accentRgb || "232,98,26";

  const region = userSettings?.region || market || "New Zealand";
  const currency = userSettings?.currency || (
    region === "Australia"      ? "AUD" :
    region === "United Kingdom" ? "GBP" :
    region === "United States"  ? "USD" :
    region === "Canada"         ? "CAD" :
    region === "Europe"         ? "EUR" :
    region === "AU" ? "AUD" : region === "UK" ? "GBP" : region === "US" ? "USD" :
    "NZD"
  );

  const [view, setView] = useState("form");
  const [intent, setIntent] = useState(initialData?.defaultIntent || "");
  const [intentOpen, setIntentOpen] = useState(false);
  // Photos: { file: File|null, preview: string, url?: string }
  // url-only entries = pre-loaded from existing catalogue record
  const [photos, setPhotos] = useState(() => {
    if (!initialData?.imageUrls?.length) return [];
    return initialData.imageUrls.map(url => ({ file: null, preview: url, url }));
  });
  const [askingPrice, setAskingPrice] = useState("");
  const [listingCondition, setListingCondition] = useState(null);
  const [listingUrl, setListingUrl] = useState("");
  const [specialDetails, setSpecialDetails] = useState("");
  const [evalLoading, setEvalLoading] = useState(false);
  const [evalError, setEvalError] = useState("");
  const [results, setResults] = useState(null);
  const [actionSaving, setActionSaving] = useState(false);
  const [clarAnswers, setClarAnswers] = useState("");
  const [clarPhotos, setClarPhotos] = useState([]);
  const [reevaling, setReevaling] = useState(false);
  const [reEvalDone, setReEvalDone] = useState(false);
  const fileInputRef = useRef(null);
  const clarFileInputRef = useRef(null);
  const scrollRef = useRef(null);

  const selectedIntent = INTENTS.find(o => o.value === intent) || null;
  const isOwned = OWNED_INTENTS.includes(intent);
  const canEvaluate = !!intent && photos.length > 0;

  const handlePhotos = (e) => {
    const files = Array.from(e.target.files || []);
    const remaining = 8 - photos.length;
    files.slice(0, remaining).forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => setPhotos(prev => [...prev, { file, preview: reader.result }]);
      reader.readAsDataURL(file);
    });
    e.target.value = "";
  };

  const removePhoto = (idx) => setPhotos(prev => prev.filter((_, i) => i !== idx));

  const buildFormData = (extraAnswers) => {
    const fd = new FormData();
    fd.append("intent", intent);
    fd.append("region", region);
    fd.append("currency", currency);
    if (askingPrice) fd.append("asking_price", askingPrice);
    if (listingCondition) fd.append("listing_condition", listingCondition);
    const noteParts = [];
    if (listingUrl) noteParts.push(`Listing URL: ${listingUrl}`);
    if (noteParts.length) fd.append("notes", noteParts.join(", "));
    if (specialDetails) fd.append("special_details", specialDetails);
    if (extraAnswers) fd.append("clarification_answers", extraAnswers);
    // Pre-loaded URL photos → pass as existing_image_urls
    const urlPhotos = photos.filter(p => !p.file && p.url).map(p => p.url);
    if (urlPhotos.length) fd.append("existing_image_urls", JSON.stringify(urlPhotos));
    // New file uploads
    photos.filter(p => p.file).forEach(p => fd.append("images", p.file));
    return fd;
  };

  const handleEvaluate = async () => {
    if (!canEvaluate) return;
    setEvalError("");
    setEvalLoading(true);
    setReEvalDone(false);
    try {
      const resp = await fetch("/api/evaluate/assess", { method: "POST", body: buildFormData() });
      const data = await resp.json();
      if (data.error) throw new Error(data.error);
      setResults(data);
      setClarAnswers("");
      setView("results");
      if (scrollRef.current) scrollRef.current.scrollTop = 0;
    } catch(e) { setEvalError(e.message || "Evaluation failed."); }
    finally { setEvalLoading(false); }
  };

  const handleClarPhotos = (e) => {
    const files = Array.from(e.target.files || []);
    const remaining = 8 - photos.length - clarPhotos.length;
    files.slice(0, remaining).forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => setClarPhotos(prev => [...prev, { file, preview: reader.result }]);
      reader.readAsDataURL(file);
    });
    e.target.value = "";
  };

  const removeClarPhoto = (idx) => setClarPhotos(prev => prev.filter((_, i) => i !== idx));

  const handleReeval = async () => {
    if (!clarAnswers.trim() && clarPhotos.length === 0) return;
    setReevaling(true);
    setEvalLoading(true);
    try {
      const fd = buildFormData(clarAnswers);
      clarPhotos.forEach(p => fd.append("images", p.file));
      const resp = await fetch("/api/evaluate/assess", { method: "POST", body: fd });
      const data = await resp.json();
      if (data.error) throw new Error(data.error);
      setResults(data);
      setClarAnswers("");
      setClarPhotos([]);
      setReEvalDone(true);
      if (scrollRef.current) scrollRef.current.scrollTop = 0;
    } catch(e) { setEvalError(e.message || "Re-evaluation failed."); }
    finally { setReevaling(false); setEvalLoading(false); }
  };

  const buildEvalData = () => ({
    model: results?.detected?.model || (results?.model_overview?.make && results?.model_overview?.model ? results.model_overview.make + " " + results.model_overview.model : "") || "",
    instrument: results?.detected?.instrument || "Bass Guitar",
    strings: results?.detected?.strings || "4-String",
    bridge: results?.detected?.bridge || "",
    serial: results?.detected?.serial || "",
    year: results?.detected?.year || results?.model_overview?.year || "",
    origin: results?.detected?.origin || results?.model_overview?.origin || "",
    condition: results?.condition?.overall || "",
    notes: "",
    listing_url: listingUrl || "",
    special_details: specialDetails || "",
    asking_price: askingPrice || "",
    listing_condition: listingCondition || "",
    imageUrls: results?.imageUrls || [],
    evaluate_result: JSON.stringify(results),
    intent,
    verdictWord: results?.intent || "",
    verdictText: results?.model_overview ? `${results.model_overview.make || ""} ${results.model_overview.model || ""}. ${results.model_overview.era_notes || ""}`.trim() : "",
    marketValue: results?.market_value ? {
      listed: results.market_value.fair_range_low && results.market_value.fair_range_high
        ? `${currency} ${results.market_value.fair_range_low}–${results.market_value.fair_range_high}`
        : null,
      fairRange: results.market_value.fair_range_low && results.market_value.fair_range_high
        ? `${currency} ${results.market_value.fair_range_low}–${results.market_value.fair_range_high}`
        : null,
      suggestedOffer: results.market_value.suggested_offer
        ? `${currency} ${results.market_value.suggested_offer}`
        : null,
      flipMargin: results.market_value.flip_margin_estimate
        ? `${currency} ${results.market_value.flip_margin_estimate}`
        : null,
    } : null,
    flags: (results?.condition?.flags || []).map(f => ({ type: "warn", text: f })),
    questions: results?.seller_questions || [],
  });

  const handlePass = () => { setView("form"); setResults(null); setClarAnswers(""); setReEvalDone(false); };

  const primaryBtnStyle = {
    padding: "14px 16px", borderRadius: 12,
    border: `1px solid ${accent}`,
    background: `rgba(${aRgb},0.1)`,
    color: accent,
    fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
  };
  const secondaryBtnStyle = {
    padding: "12px 16px", borderRadius: 12,
    border: `1px solid rgba(88,84,80,0.35)`,
    background: "transparent",
    color: DIM, fontSize: 13, cursor: "pointer", fontFamily: "inherit",
  };

  const inputSt = {
    width: "100%", background: SURFACE, border: `1px solid ${BORDER}`,
    borderRadius: 10, padding: "12px 14px", color: TEXT, fontSize: 14,
    fontFamily: "inherit", outline: "none", boxSizing: "border-box",
  };

  // ── FORM VIEW ─────────────────────────────────────────────────────────────────
  const formView = (
    <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch", paddingBottom: 16 }}>
      <div style={{ maxWidth: 900, margin: "0 auto", padding: "20px 20px 0" }}>

        <div style={{ fontSize: 22, fontWeight: 700, color: TEXT, marginBottom: 6 }}>Evaluate</div>
        <div style={{ fontSize: 14, color: MUTED, lineHeight: 1.5, marginBottom: 24 }}>
          Add photos and our AI will do the rest. You'll get a full analysis tuned to your intent.
        </div>
        <div style={{ height: 1, background: BORDER, marginBottom: 24 }} />

        {/* ── Photos ── */}
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", color: TEXT, textTransform: "uppercase", marginBottom: 8 }}>
          Photos <span style={{ color: "#ef4444" }}>*</span>
        </div>
        <div style={{ fontSize: 12, color: DIM, marginBottom: 10 }}>Up to 8 — headstock, neck, body, bridge, any damage</div>

        {photos.length === 0 ? (
          <>
            <button onClick={() => fileInputRef.current?.click()} style={{
              background: SURFACE, border: `1.5px dashed ${BORDER}`, borderRadius: 12, padding: 20,
              display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
              cursor: "pointer", width: "100%", fontFamily: "inherit", marginBottom: isOwned ? 10 : 24,
            }}>
              <span style={{ fontSize: 26, opacity: 0.45 }}>📷</span>
              <span style={{ fontSize: 14, fontWeight: 600, color: MUTED }}>Add Photos</span>
              <span style={{ fontSize: 11, color: DIM }}>Opens your photo library, camera or files</span>
            </button>
            {isOwned && (
              <div style={{ marginBottom: 24, padding: "10px 14px", background: `rgba(${aRgb},0.06)`, border: `1px solid rgba(${aRgb},0.25)`, borderRadius: 10, fontSize: 13, color: MUTED, lineHeight: 1.5 }}>
                📸 Photos help a lot — even a quick shot of the headstock makes the assessment sharper. You can still proceed without them.
              </div>
            )}
          </>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 24 }}>
            {photos.map((p, i) => (
              <div key={i} style={{ position: "relative", aspectRatio: "1", borderRadius: 8, overflow: "visible", background: "#1a1a18", border: `1px solid ${BORDER}` }}>
                <img src={p.preview} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 8 }} />
                {p.url && !p.file && (
                  <div style={{ position: "absolute", bottom: 2, left: 2, background: "rgba(0,0,0,0.7)", borderRadius: 4, padding: "1px 4px", fontSize: 8, color: MUTED }}>saved</div>
                )}
                <button onClick={() => removePhoto(i)} style={{ position: "absolute", top: -4, right: -4, background: "#e05050", border: "none", borderRadius: "50%", width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "#fff", fontWeight: 800, cursor: "pointer", padding: 0, zIndex: 1 }}>×</button>
              </div>
            ))}
            {photos.length < 8 && (
              <button onClick={() => fileInputRef.current?.click()} style={{ aspectRatio: "1", background: SURFACE, border: `1.5px dashed ${BORDER}`, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, color: DIM, cursor: "pointer", fontFamily: "inherit" }}>+</button>
            )}
          </div>
        )}
        <input ref={fileInputRef} type="file" accept="image/*" multiple style={{ display: "none" }} onChange={handlePhotos} />

        <div style={{ height: 1, background: BORDER, marginBottom: 24 }} />

        {/* ── Intent dropdown ── */}
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", color: TEXT, textTransform: "uppercase", marginBottom: 10 }}>
          What are you evaluating for? <span style={{ color: "#ef4444" }}>*</span>
        </div>
        <div style={{ position: "relative", marginBottom: 24 }}>
          <button
            onClick={() => setIntentOpen(o => !o)}
            style={{
              width: "100%", display: "flex", alignItems: "center", gap: 12,
              padding: "13px 14px", borderRadius: 12, cursor: "pointer",
              fontFamily: "inherit", textAlign: "left",
              background: SURFACE,
              border: `1px solid ${intent ? `rgba(${aRgb},0.5)` : BORDER}`,
            }}
          >
            {selectedIntent ? (
              <>
                {selectedIntent.icon}
                <span style={{ flex: 1, fontSize: 14, fontWeight: 600, color: accent }}>{selectedIntent.label}</span>
              </>
            ) : (
              <span style={{ flex: 1, fontSize: 14, color: MUTED }}>Select intent…</span>
            )}
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke={DIM} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, transition: "transform 0.15s", transform: intentOpen ? "rotate(180deg)" : "none" }}>
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </button>
          {intentOpen && (
            <div style={{
              position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 50,
              background: "#1a1a18", border: `1px solid ${BORDER}`, borderRadius: 12,
              overflow: "hidden", boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
            }}>
              {INTENTS.map((opt, idx) => (
                <button
                  key={opt.value}
                  onClick={() => { setIntent(opt.value); setIntentOpen(false); }}
                  style={{
                    width: "100%", display: "flex", alignItems: "center", gap: 12,
                    padding: "12px 14px", cursor: "pointer", fontFamily: "inherit", textAlign: "left",
                    background: intent === opt.value ? `rgba(${aRgb},0.08)` : "transparent",
                    border: "none",
                    borderBottom: idx < INTENTS.length - 1 ? `1px solid ${BORDER}` : "none",
                  }}
                >
                  {opt.icon}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: intent === opt.value ? accent : TEXT }}>{opt.label}</div>
                    <div style={{ fontSize: 11, color: DIM, marginTop: 1 }}>{opt.desc}</div>
                  </div>
                  {intent === opt.value && (
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke={accent} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        <div style={{ height: 1, background: BORDER, marginBottom: 24 }} />

        {/* ── Asking Price ── */}
        {!isOwned && (
          <>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", color: TEXT, textTransform: "uppercase", marginBottom: 8 }}>Asking price <span style={{ fontSize: 10, fontWeight: 400, color: DIM, letterSpacing: 0, textTransform: "none" }}>(optional — what the seller is asking)</span></div>
            <input
              type="number" min="0" value={askingPrice} onChange={e => setAskingPrice(e.target.value)}
              placeholder="e.g. 450"
              style={{ ...inputSt, marginBottom: 24 }}
            />

            {/* ── Condition ── */}
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", color: TEXT, textTransform: "uppercase", marginBottom: 8 }}>Condition</div>
            <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
              {[{ label: "New", val: "new" }, { label: "Secondhand", val: "secondhand" }].map(({ label, val }) => (
                <button
                  key={val}
                  onClick={() => setListingCondition(p => p === val ? null : val)}
                  style={{
                    flex: 1, padding: "10px 8px", borderRadius: 10, cursor: "pointer",
                    fontFamily: "inherit", fontSize: 13, fontWeight: 600,
                    border: `1px solid ${listingCondition === val ? `rgba(${aRgb},0.5)` : BORDER}`,
                    background: listingCondition === val ? `rgba(${aRgb},0.1)` : SURFACE,
                    color: listingCondition === val ? accent : MUTED,
                  }}
                >{label}</button>
              ))}
            </div>
          </>
        )}

        {/* ── Listing URL ── */}
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", color: TEXT, textTransform: "uppercase", marginBottom: 8 }}>Listing URL <span style={{ fontSize: 10, fontWeight: 400, color: DIM, letterSpacing: 0, textTransform: "none" }}>(optional, for reference only)</span></div>
        <div style={{
          display: "flex", alignItems: "center",
          background: SURFACE, border: `1px solid ${listingUrl.length > 8 ? `rgba(${aRgb},0.5)` : BORDER}`,
          borderRadius: 12, padding: "0 14px", gap: 10, marginBottom: 8,
        }}>
          <span style={{ color: MUTED, fontSize: 15, flexShrink: 0 }}>🔗</span>
          <input
            type="url" value={listingUrl} onChange={e => setListingUrl(e.target.value)}
            placeholder="Paste listing URL (stored for reference)"
            style={{ flex: 1, background: "none", border: "none", outline: "none", color: TEXT, fontSize: 14, padding: "14px 0", fontFamily: "inherit" }}
          />
          {listingUrl.length > 8 && (
            <button onClick={() => setListingUrl("")} style={{ background: "none", border: "none", color: DIM, fontSize: 18, cursor: "pointer", padding: 0, lineHeight: 1 }}>×</button>
          )}
        </div>

        {/* ── Special Details ── */}
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", color: TEXT, textTransform: "uppercase", marginBottom: 8 }}>Special details <span style={{ fontSize: 10, fontWeight: 400, color: DIM, letterSpacing: 0, textTransform: "none" }}>(optional)</span></div>
        <textarea
          value={specialDetails} onChange={e => setSpecialDetails(e.target.value)}
          placeholder="e.g. signature model, artist edition, known mods, limited run"
          rows={2}
          style={{ ...inputSt, resize: "vertical", marginBottom: 24 }}
        />

        {evalError && (
          <div style={{ marginTop: 12, padding: "10px 14px", background: "#1a0c0c", border: "1px solid #3d1e1e", borderRadius: 8, fontSize: 13, color: "#e06060" }}>{evalError}</div>
        )}
      </div>
    </div>
  );

  // ── RESULTS VIEW ──────────────────────────────────────────────────────────────
  const resultsView = results && (
    <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch", paddingBottom: 20 }}>
      <div style={{ maxWidth: 900, margin: "0 auto" }}>

        {/* ── Out of scope ── */}
        {results.out_of_scope ? (
          <div style={{ padding: "40px 20px" }}>
            <div style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "24px 20px", textAlign: "center" }}>
              <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke={accent} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 14 }}><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>
              <div style={{ fontSize: 18, fontWeight: 700, color: TEXT, marginBottom: 10 }}>Guitars and Basses Only</div>
              <div style={{ fontSize: 14, color: MUTED, lineHeight: 1.6, marginBottom: 24 }}>
                {results.out_of_scope_message || "StrumIQ evaluates guitars and basses only. Please submit a guitar or bass for evaluation."}
              </div>
              <button onClick={handlePass} style={{ ...primaryBtnStyle, width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                Back to Evaluate
              </button>
            </div>
          </div>
        ) : (() => {
          const lowConf = results.confidence_score != null && results.confidence_score < 60;
          const hasClarQ = results.clarification_questions?.length > 0;

          // Low confidence — first attempt: show questions only
          if (lowConf && !reEvalDone) {
            return (
              <div style={{ padding: "20px 20px 4px" }}>
                <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.5px", color: TEXT, marginBottom: 8 }}>More Information Needed</div>
                <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 14px", borderRadius: 20, background: confidenceBg(results.confidence_score), border: `1px solid ${confidenceColor(results.confidence_score)}40`, marginBottom: 16 }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: confidenceColor(results.confidence_score), flexShrink: 0 }} />
                  <span style={{ fontSize: 13, fontWeight: 700, color: confidenceColor(results.confidence_score) }}>{results.confidence_score}% confidence</span>
                </div>
                <div style={{ padding: "12px 16px", background: "#140e00", border: "1px solid rgba(245,158,11,0.3)", borderRadius: 12, marginBottom: 16, fontSize: 14, color: MUTED, lineHeight: 1.6 }}>
                  We need a little more information to give you a reliable assessment. Please answer the questions below and re-evaluate.
                </div>
                {hasClarQ && (
                  <div style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "14px 16px", marginBottom: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#f59e0b", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Please answer these questions</div>
                    {results.clarification_questions.map((q, i) => (
                      <div key={i} style={{ fontSize: 13, color: MUTED, marginBottom: 6, display: "flex", gap: 8 }}>
                        <span style={{ color: "#f59e0b", flexShrink: 0 }}>{i+1}.</span>{q}
                      </div>
                    ))}

                    {/* Additional photo upload — matches main evaluate form style */}
                    <div style={{ marginTop: 14, marginBottom: 4, fontSize: 11, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: "0.08em" }}>Add Photos <span style={{ fontSize: 10, fontWeight: 400, color: DIM, letterSpacing: 0, textTransform: "none" }}>(optional — helps with identification)</span></div>
                    {clarPhotos.length === 0 ? (
                      <button onClick={() => clarFileInputRef.current?.click()} style={{
                        background: SURFACE, border: `1.5px dashed ${BORDER}`, borderRadius: 12, padding: 20,
                        display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
                        cursor: "pointer", width: "100%", fontFamily: "inherit", marginBottom: 10,
                      }}>
                        <span style={{ fontSize: 26, opacity: 0.45 }}>📷</span>
                        <span style={{ fontSize: 14, fontWeight: 600, color: MUTED }}>Add Photos</span>
                        <span style={{ fontSize: 11, color: DIM }}>Opens your photo library, camera or files</span>
                      </button>
                    ) : (
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 10 }}>
                        {clarPhotos.map((p, i) => (
                          <div key={i} style={{ position: "relative", aspectRatio: "1", borderRadius: 8, overflow: "visible", background: "#1a1a18", border: `1px solid ${BORDER}` }}>
                            <img src={p.preview} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 8 }} />
                            <button onClick={() => removeClarPhoto(i)} style={{ position: "absolute", top: -4, right: -4, background: "#e05050", border: "none", borderRadius: "50%", width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "#fff", fontWeight: 800, cursor: "pointer", padding: 0, zIndex: 1 }}>×</button>
                          </div>
                        ))}
                        {clarPhotos.length < 8 && (
                          <button onClick={() => clarFileInputRef.current?.click()} style={{ aspectRatio: "1", background: SURFACE, border: `1.5px dashed ${BORDER}`, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, color: DIM, cursor: "pointer", fontFamily: "inherit" }}>+</button>
                        )}
                      </div>
                    )}
                    <input ref={clarFileInputRef} type="file" accept="image/*" multiple style={{ display: "none" }} onChange={handleClarPhotos} />

                    <textarea
                      value={clarAnswers} onChange={e => setClarAnswers(e.target.value)}
                      rows={3} placeholder="Type your answers here…"
                      style={{ width: "100%", background: "#0a0a0a", border: `1px solid ${BORDER}`, borderRadius: 8, padding: "10px 12px", color: TEXT, fontSize: 13, fontFamily: "inherit", outline: "none", resize: "vertical", boxSizing: "border-box", marginTop: 4 }}
                    />
                    <button
                      onClick={handleReeval} disabled={(!clarAnswers.trim() && clarPhotos.length === 0) || reevaling}
                      style={{ marginTop: 8, width: "100%", padding: "10px", borderRadius: 10, border: "1px solid rgba(245,158,11,0.5)", background: (clarAnswers.trim() || clarPhotos.length > 0) ? "rgba(245,158,11,0.1)" : "transparent", color: (clarAnswers.trim() || clarPhotos.length > 0) ? "#f59e0b" : DIM, fontFamily: "inherit", fontSize: 13, fontWeight: 600, cursor: (clarAnswers.trim() || clarPhotos.length > 0) ? "pointer" : "not-allowed" }}
                    >{reevaling ? "Re-evaluating…" : "Re-evaluate with Answers"}</button>
                  </div>
                )}
                <button onClick={handlePass} style={{ ...secondaryBtnStyle, width: "100%", marginBottom: 20, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}><svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>Back</button>
              </div>
            );
          }

          // Low confidence — re-eval still failed
          if (lowConf && reEvalDone) {
            return (
              <div style={{ padding: "40px 20px" }}>
                <div style={{ background: SURFACE, border: `1px solid ${BORDER}`, borderRadius: 14, padding: "24px 20px", textAlign: "center" }}>
                  <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke={MUTED} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 14 }}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                  <div style={{ fontSize: 18, fontWeight: 700, color: TEXT, marginBottom: 10 }}>Not Enough to Go On</div>
                  <div style={{ fontSize: 14, color: MUTED, lineHeight: 1.6, marginBottom: 24 }}>
                    There isn't enough information to complete a reliable assessment for this instrument. Try adding clearer photos or more detail — a clear headstock shot makes the biggest difference.
                  </div>
                  <button onClick={handlePass} style={{ ...primaryBtnStyle, width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                    Try Again
                  </button>
                </div>
              </div>
            );
          }

          // Full results (confidence >= 60)
          return (
            <>
              <div style={{ padding: "20px 20px 4px" }}>
                <div style={{ fontSize: 13, color: MUTED, marginBottom: 4 }}>
                  {results.model_overview?.make && results.model_overview?.model
                    ? `${results.model_overview.make} ${results.model_overview.model}`
                    : results.detected?.model || "Instrument"}
                </div>
                <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.5px", color: TEXT, marginBottom: 12 }}>Evaluation Complete</div>

                {results.confidence_score != null && (
                  <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 14px", borderRadius: 20, background: confidenceBg(results.confidence_score), border: `1px solid ${confidenceColor(results.confidence_score)}40`, marginBottom: 16 }}>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: confidenceColor(results.confidence_score), flexShrink: 0 }} />
                    <span style={{ fontSize: 13, fontWeight: 700, color: confidenceColor(results.confidence_score) }}>{results.confidence_score}% confidence</span>
                  </div>
                )}
              </div>

              <div style={{ padding: "0 20px", display: "flex", flexDirection: "column", gap: 12 }}>

                {results.model_overview && (results.model_overview.make || results.model_overview.era_notes) && (
                  <ResultCard header="📖 Model Overview">
                    {results.model_overview.make && <KVRow label="Make" value={results.model_overview.make} />}
                    {results.model_overview.model && <KVRow label="Model" value={results.model_overview.model} />}
                    {results.model_overview.year && <KVRow label="Year" value={results.model_overview.year} />}
                    {results.model_overview.origin && <KVRow label="Origin" value={results.model_overview.origin} />}
                    {results.model_overview.era_notes && <div style={{ fontSize: 13, color: MUTED, lineHeight: 1.6, paddingTop: 8, borderTop: `1px solid ${BORDER}` }}>{results.model_overview.era_notes}</div>}
                  </ResultCard>
                )}

                {results.hardware_overview && (results.hardware_overview.pickups || results.hardware_overview.bridge) && (
                  <ResultCard header="⚙️ Hardware">
                    {results.hardware_overview.pickups && <KVRow label="Pickups" value={results.hardware_overview.pickups} />}
                    {results.hardware_overview.bridge && <KVRow label="Bridge" value={results.hardware_overview.bridge} />}
                    {results.hardware_overview.tuners && <KVRow label="Tuners" value={results.hardware_overview.tuners} />}
                    {results.hardware_overview.nut_material && <KVRow label="Nut" value={results.hardware_overview.nut_material} />}
                    {results.hardware_overview.notable_specs && <KVRow label="Notable" value={results.hardware_overview.notable_specs} last />}
                  </ResultCard>
                )}

                {results.condition && (
                  <ResultCard header="🔍 Condition">
                    {results.condition.overall && (
                      <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 14px", borderRadius: 20, background: conditionBg(results.condition.overall), border: `1px solid ${conditionColor(results.condition.overall)}40`, marginBottom: results.condition.flags?.length ? 10 : 0 }}>
                        <div style={{ width: 8, height: 8, borderRadius: "50%", background: conditionColor(results.condition.overall) }} />
                        <span style={{ fontSize: 14, fontWeight: 700, color: conditionColor(results.condition.overall) }}>{results.condition.overall}</span>
                      </div>
                    )}
                    {results.condition.flags?.length > 0 && results.condition.flags.map((f, i) => (
                      <div key={i} style={{ display: "flex", gap: 8, fontSize: 13, color: MUTED, padding: "5px 0", borderBottom: i < results.condition.flags.length - 1 ? `1px solid ${BORDER}` : "none" }}>
                        <span style={{ color: "#f59e0b", flexShrink: 0 }}>⚑</span>{f}
                      </div>
                    ))}
                  </ResultCard>
                )}

                {results.setup_profile && (results.setup_profile.known_quirks || results.setup_profile.recommended_action_range) && (
                  <ResultCard header="🎛 Setup Profile">
                    {results.setup_profile.string_gauges && <KVRow label="String gauges" value={results.setup_profile.string_gauges} />}
                    {results.setup_profile.nut_material && <KVRow label="Nut material" value={results.setup_profile.nut_material} />}
                    {results.setup_profile.recommended_action_range && <KVRow label="Action range" value={results.setup_profile.recommended_action_range} />}
                    {results.setup_profile.known_quirks && <div style={{ fontSize: 13, color: MUTED, lineHeight: 1.6, paddingTop: 8, borderTop: `1px solid ${BORDER}` }}>{results.setup_profile.known_quirks}</div>}
                  </ResultCard>
                )}

                {results.market_value && (results.market_value.fair_range_low || results.market_value.suggested_offer) && (
                  <ResultCard header="💰 Market Value">
                    {results.market_value.fair_range_low && results.market_value.fair_range_high && (
                      <KVRow label="Fair market range" value={`${results.market_value.currency || currency} ${results.market_value.fair_range_low}–${results.market_value.fair_range_high}`} color={accent} />
                    )}
                    {results.market_value.suggested_offer && (
                      <KVRow label="Suggested offer" value={`${results.market_value.currency || currency} ${results.market_value.suggested_offer}`} color="#5ab870" />
                    )}
                    {intent === "flip" && results.market_value.flip_margin_estimate && (
                      <KVRow label="Flip margin (est.)" value={`${results.market_value.currency || currency} ${results.market_value.flip_margin_estimate}`} last />
                    )}
                  </ResultCard>
                )}

                {(intent === "buy_self" || intent === "flip") && results.seller_questions?.length > 0 && (
                  <ResultCard header="❓ Ask the Seller">
                    {results.seller_questions.map((q, i) => (
                      <div key={i} style={{ fontSize: 13, color: MUTED, paddingLeft: 12, position: "relative", lineHeight: 1.4, marginBottom: i < results.seller_questions.length - 1 ? 6 : 0 }}>
                        <span style={{ position: "absolute", left: 0, color: accent }}>·</span>{q}
                      </div>
                    ))}
                  </ResultCard>
                )}

                {results.confidence_flags?.length > 0 && (
                  <ResultCard header="⚠ Confidence Flags">
                    {results.confidence_flags.map((f, i) => (
                      <div key={i} style={{ fontSize: 13, color: MUTED, padding: "5px 0", borderBottom: i < results.confidence_flags.length - 1 ? `1px solid ${BORDER}` : "none" }}>{f}</div>
                    ))}
                  </ResultCard>
                )}
              </div>

              {/* ── Intent-aware CTAs ── */}
              <div style={{ padding: "20px 20px 0" }}>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: DIM, marginBottom: 14 }}>
                  {assessTarget ? `Save to ${assessTarget.name}` : "What do you want to do?"}
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
                  {/* buy_self — primary + watchlist + pass */}
                  {intent === "buy_self" && (
                    <>
                      <button onClick={() => onNavigate("gear", buildEvalData())} style={{ ...primaryBtnStyle, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>
                        Add to My Gear
                      </button>
                      <button onClick={() => onNavigate("watch", buildEvalData())} disabled={actionSaving} style={{ ...secondaryBtnStyle, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                        Add to Watchlist
                      </button>
                      <button onClick={handlePass} style={{ ...secondaryBtnStyle, color: DIM, opacity: 0.7 }}>✕ Pass — not for me</button>
                    </>
                  )}

                  {/* flip */}
                  {intent === "flip" && (
                    <>
                      <button onClick={() => onNavigate("flip", buildEvalData())} style={{ ...primaryBtnStyle, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 16V4m0 0L3 8m4-4 4 4"/><path d="M17 8v12m0 0 4-4m-4 4-4-4"/></svg>
                        Add to Flips
                      </button>
                      <button onClick={() => onNavigate("watch", buildEvalData())} disabled={actionSaving} style={{ ...secondaryBtnStyle, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                        Add to Watchlist
                      </button>
                      <button onClick={handlePass} style={{ ...secondaryBtnStyle, color: DIM, opacity: 0.7 }}>✕ Pass — not for me</button>
                    </>
                  )}

                  {/* own — update existing or add new */}
                  {intent === "own" && (
                    <>
                      {assessTarget ? (
                        <button onClick={() => onSaveAssessment && onSaveAssessment(buildEvalData())} disabled={actionSaving} style={primaryBtnStyle}>
                          Update My Gear Record
                        </button>
                      ) : (
                        <button onClick={() => onNavigate("gear", buildEvalData())} style={{ ...primaryBtnStyle, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>
                          Add to My Gear
                        </button>
                      )}
                      <button onClick={handlePass} style={secondaryBtnStyle}>Cancel</button>
                    </>
                  )}

                  {/* reassess */}
                  {intent === "reassess" && (
                    <>
                      <button
                        onClick={assessTarget
                          ? () => onSaveAssessment && onSaveAssessment(buildEvalData())
                          : () => onNavigate("gear", buildEvalData())}
                        disabled={actionSaving}
                        style={primaryBtnStyle}
                      >
                        Update My Gear Record
                      </button>
                      <button onClick={handlePass} style={secondaryBtnStyle}>Cancel</button>
                    </>
                  )}

                  {/* mates_gear */}
                  {intent === "mates_gear" && (
                    <>
                      {assessTarget ? (
                        <button onClick={() => onSaveAssessment && onSaveAssessment(buildEvalData())} disabled={actionSaving} style={primaryBtnStyle}>
                          Save to Mates Gear
                        </button>
                      ) : (
                        <button onClick={() => onNavigate("mates", buildEvalData())} style={primaryBtnStyle}>
                          Save to Mates Gear
                        </button>
                      )}
                      <button onClick={handlePass} style={secondaryBtnStyle}>Cancel</button>
                    </>
                  )}
                </div>

                <div style={{ fontSize: 11, color: DIM, textAlign: "center", lineHeight: 1.5, padding: "0 8px 28px" }}>
                  AI assessment is a guide only — not a substitute for hands-on inspection.
                </div>
              </div>
            </>
          );
        })()}
      </div>
    </div>
  );

  return (
    <div style={{ flex: 1, background: BG, color: TEXT, display: "flex", flexDirection: "column", fontFamily: "inherit", position: "relative", minHeight: 0 }}>
      {view === "form" ? formView : resultsView}
      {view === "form" && (
        <div style={{ flexShrink: 0 }}>
          <div style={{ maxWidth: 900, margin: "0 auto", padding: "4px 16px 28px" }}>
            <button
              onClick={handleEvaluate}
              disabled={!canEvaluate || evalLoading}
              style={{
                width: "100%",
                background: canEvaluate && !evalLoading ? `rgba(${aRgb},0.1)` : "#161614",
                border: `1px solid ${canEvaluate && !evalLoading ? accent : BORDER}`,
                borderRadius: 12, padding: "15px 0",
                color: canEvaluate && !evalLoading ? accent : DIM,
                fontSize: 15, fontWeight: 600,
                cursor: canEvaluate && !evalLoading ? "pointer" : "not-allowed",
                fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
              }}
            >
              {evalLoading ? "Evaluating…" : (
                <><span>Evaluate</span><span style={{ background: canEvaluate ? `rgba(${aRgb},0.2)` : "rgba(255,255,255,0.05)", borderRadius: 6, padding: "2px 7px", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em" }}>AI</span></>
              )}
            </button>
            {!canEvaluate && !evalLoading && (
              <div style={{ textAlign: "center", fontSize: 11, color: DIM, marginTop: 8 }}>
                {!photos.length && !intent ? "Add a photo and select an intent to evaluate" : !photos.length ? "Add at least one photo to evaluate" : "Select an intent to evaluate"}
              </div>
            )}
            {!assessTarget && (
              <div style={{ textAlign: "center", marginTop: 14 }}>
                <button onClick={() => onNavigate && onNavigate("manual")} style={{ background: "none", border: "none", color: DIM, fontSize: 12, cursor: "pointer", fontFamily: "inherit", textDecoration: "underline", textDecorationColor: "rgba(88,84,80,0.4)" }}>
                  Add manually instead
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      {evalLoading && (
        <div style={{ position: "absolute", inset: 0, background: BG, zIndex: 50, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 0 }}>
          <style>{`@keyframes evalSpin { to { transform: rotate(360deg); } }`}</style>
          <div style={{ width: 64, height: 64, border: "3px solid #222", borderTopColor: accent, borderRadius: "50%", animation: "evalSpin 0.85s linear infinite", marginBottom: 36 }} />
          <div style={{ color: TEXT, fontSize: 30, fontWeight: 700, letterSpacing: "-0.5px", marginBottom: 12 }}>Evaluating…</div>
          <div style={{ color: MUTED, fontSize: 16, marginBottom: 40 }}>This usually takes 15–30 seconds</div>
          <div style={{ color: DIM, fontSize: 13, textAlign: "center", maxWidth: 280, lineHeight: 1.7, padding: "0 24px", borderTop: `1px solid ${BORDER}`, paddingTop: 28 }}>AI assessment is a guide only — not a substitute for hands-on inspection.</div>
        </div>
      )}
    </div>
  );
}
